import { useState } from 'react'
import { motion } from 'framer-motion'
import { X, User, Mail, AlertTriangle, CheckCircle, Loader, Heart, HeartOff, UserCheck } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface CandidateFormModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface CandidateFormData {
  email: string
  first_name: string
  last_name: string
}

interface ExistingUser {
  user_uuid: string
  first_name: string
  last_name: string
  email: string
  is_following?: boolean
}

export default function CandidateFormModal({ open, onClose, onSuccess }: CandidateFormModalProps) {
  const [formData, setFormData] = useState<CandidateFormData>({
    email: '',
    first_name: '',
    last_name: ''
  })
  const [loading, setLoading] = useState(false)
  const [emailValidating, setEmailValidating] = useState(false)
  const [emailValidated, setEmailValidated] = useState(false)
  const [emailExists, setEmailExists] = useState(false)
  const [existingUser, setExistingUser] = useState<ExistingUser | null>(null)
  const [message, setMessage] = useState<{text: string, type: 'success' | 'error' | 'info'} | null>(null)
  const [followLoading, setFollowLoading] = useState(false)

  // Helper function to safely extract error message from API response
  const getErrorMessage = (response: any, fallback: string): string => {
    if (typeof response?.error === 'string') return response.error
    if (typeof response?.message === 'string') return response.message
    if (typeof response?.error?.message === 'string') return response.error.message
    return fallback
  }

  // Helper function to safely set message ensuring text is always a string
  const setMessageSafely = (text: any, type: 'success' | 'error' | 'info') => {
    setMessage({
      text: typeof text === 'string' ? text : String(text),
      type
    })
  }

  const validateEmail = async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailValidated(false)
      setEmailExists(false)
      setExistingUser(null)
      return
    }

    setEmailValidating(true)
    setMessage(null)

    try {
      const response = await apiClient.validateCandidateEmail(email)
      
      const responseData = response.data as any
      
      // Check if user exists based on the response data
      const userExists = responseData?.user_exists
      
      if (!userExists) {
        // Case 1: Email is available - user_exists: false
        setEmailValidated(true)
        setEmailExists(false)
        setExistingUser(null)
        setMessage({
          text: 'Email is available. Please enter candidate details.',
          type: 'success'
        })
      } else if (userExists) {
        // Case 2: Candidate already exists - success: false, user_exists: true
        setEmailValidated(true)
        setEmailExists(true)
        
        const existingData: ExistingUser = {
          user_uuid: String(responseData.candidate_uuid || ''),
          first_name: String(responseData.first_name || ''),
          last_name: String(responseData.last_name || ''),
          email: String(email),
          is_following: Boolean(responseData.is_following)
        }
        
        setExistingUser(existingData)
        setMessage({
          text: 'Candidate found! You can follow them below.',
          type: 'info'
        })
      }
    } catch (error: any) {
      console.error('Error validating email:', error)
      setMessage({
        text: typeof error?.message === 'string' ? error.message : 'Failed to validate email',
        type: 'error'
      })
      setEmailValidated(false)
    } finally {
      setEmailValidating(false)
    }
  }

  const handleEmailBlur = () => {
    if (formData.email) {
      validateEmail(formData.email)
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value
    setFormData({...formData, email})
    
    // Reset validation state when email changes
    if (emailValidated) {
      setEmailValidated(false)
      setEmailExists(false)
      setExistingUser(null)
      setMessage(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Prevent submission if candidate already exists
    if (emailExists) {
      setMessage({
        text: 'This candidate is already in your system. Use the follow button to track them.',
        type: 'info'
      })
      return
    }
    
    if (!emailValidated) {
      setMessage({
        text: 'Please validate the email address first',
        type: 'error'
      })
      return
    }
    
    if (!formData.first_name || !formData.last_name) {
      setMessage({
        text: 'Please fill in all required fields',
        type: 'error'
      })
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      const response = await apiClient.createCandidateFromForm(formData)
      
      if (response.success) {
        setMessage({
          text: 'Candidate created successfully!',
          type: 'success'
        })
        
        setTimeout(() => {
          onSuccess?.()
          onClose()
          resetForm()
        }, 2000)
      } else {
        setMessageSafely(getErrorMessage(response, 'Failed to create candidate'), 'error')
      }
    } catch (error: any) {
      console.error('Error creating candidate:', error)
      setMessage({
        text: typeof error?.message === 'string' ? error.message : 'Failed to create candidate',
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleFollow = async () => {
    if (!existingUser) return
    
    setFollowLoading(true)
    try {
      const response = await apiClient.followCandidate(existingUser.user_uuid)
      
      if (response.success) {
        setExistingUser({
          ...existingUser,
          is_following: true
        })
        setMessage({
          text: 'Successfully following candidate',
          type: 'success'
        })
        
        // Close modal after successful follow action and notify parent
        setTimeout(() => {
          onSuccess?.()
          onClose()
          resetForm()
        }, 1500)
      } else {
        setMessageSafely(getErrorMessage(response, 'Failed to follow candidate'), 'error')
      }
    } catch (error: any) {
      setMessage({
        text: 'Failed to follow candidate',
        type: 'error'
      })
    } finally {
      setFollowLoading(false)
    }
  }

  const handleUnfollow = async () => {
    if (!existingUser) return
    
    setFollowLoading(true)
    try {
      const response = await apiClient.unfollowCandidate(existingUser.user_uuid)
      
      if (response.success) {
        setExistingUser({
          ...existingUser,
          is_following: false
        })
        setMessage({
          text: 'Successfully unfollowed candidate',
          type: 'success'
        })
      } else {
        setMessageSafely(getErrorMessage(response, 'Failed to unfollow candidate'), 'error')
      }
    } catch (error: any) {
      setMessage({
        text: 'Failed to unfollow candidate',
        type: 'error'
      })
    } finally {
      setFollowLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: ''
    })
    setEmailValidated(false)
    setEmailExists(false)
    setExistingUser(null)
    setMessage(null)
    setFollowLoading(false)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Add New Candidate</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200'
                : message.type === 'info'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertTriangle size={16} />
            )}
            <span className="text-sm">{message.text}</span>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              {emailValidating && (
                <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 animate-spin" size={16} />
              )}
              <input
                type="email"
                value={formData.email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="candidate@example.com"
                required
                disabled={emailValidating}
              />
            </div>
          </div>

          {existingUser && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                {/* Profile Picture Placeholder */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center border-2 border-blue-200">
                    <User size={24} className="text-blue-600" />
                  </div>
                </div>
                
                {/* Candidate Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {String(existingUser.first_name)} {String(existingUser.last_name)}
                    </h3>
                    {existingUser.is_following ? (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg mr-2">
                          <UserCheck size={16} />
                          <span className="text-sm font-medium">Following</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleUnfollow}
                          disabled={followLoading}
                          className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {followLoading ? (
                            <Loader size={14} className="animate-spin" />
                          ) : (
                            <HeartOff size={14} />
                          )}
                          <span className="text-xs">Unfollow</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleFollow}
                        disabled={followLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {followLoading ? (
                          <Loader size={16} className="animate-spin" />
                        ) : (
                          <Heart size={16} />
                        )}
                        <span>Follow</span>
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-gray-400" />
                      <span>{String(existingUser.email)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    {existingUser.is_following ? (
                      <p className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full inline-block">
                        ✓ You're already following this candidate
                      </p>
                    ) : (
                      <p className="text-sm text-blue-600">
                        This candidate is already in your system. Click follow to start tracking them.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Only show name fields if email doesn't exist or is not validated */}
          {!emailExists && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="John"
                    required
                    disabled={!emailValidated}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Doe"
                    required
                    disabled={!emailValidated}
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            {!emailExists && (
              <button
                type="submit"
                disabled={loading || !emailValidated || emailExists}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Add Candidate'}
              </button>
            )}
          </div>
        </form>
      </motion.div>
    </div>
  )
}